import {
	Button,
	ControlledCheckbox,
	ControlledRadioGroup,
	Input,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@dune/ui";
import { ArrowLeft, ArrowRight } from "@untitled-ui/icons-react";
import { useFormContext, useWatch } from "react-hook-form";
import { isClientUsers } from "../../../utils/clients";
import NewClientForm from "./NewClientForm";
import {
	CreateConstructionSiteClientFormData,
	createConstructionSiteSchema,
} from "./useCreateConstructionSiteForm";
import { useMemo, useState } from "react";
import SendQuizToCreateConstructionByClientDialog from "./QuizzToCreateConstructionByClientDialog";
import IsSyndicContainer from "./IsSyndicContainer";
import { useMe } from "../../../api/queries/users.queries";
import { useClientAutoSelection } from "./useClientAutoSelection";
import { SelectedClient } from "../../../pages/constructionSites/projectForm/CommonProjectForm";
import { RoleEnum } from "../../../../../../packages/vars/src";
import AddressFormFields from "../../shared/AddressFormFields";

type ClientDetailsContainerProps = {
	client: SelectedClient;
	setSelectedClient: (client: SelectedClient) => void;
	canValidate: boolean;
	currentUserIsClient: boolean;
	excludeSyndics: boolean;
	initialClientSocietyId: number | null;
};

const ClientDetailsContainer = ({
	client,
	setSelectedClient,
	canValidate,
	currentUserIsClient,
	excludeSyndics,
	initialClientSocietyId,
}: ClientDetailsContainerProps) => {
	const { me } = useMe();
	const canUpdateExistingSociety = useMemo(
		() => me?.role === RoleEnum.DUNE,
		[me?.role],
	);
	const { control, reset, trigger, getValues } =
		useFormContext<CreateConstructionSiteClientFormData>();
	const [isModalToQuizzOpen, setIsModalToQuizzOpen] = useState(false);

	const hasHeadOfficeAddress = useMemo(
		() =>
			!isClientUsers(client) &&
			client &&
			"headOfficeAddress" in client &&
			client.headOfficeAddress !== null &&
			client.headOfficeAddress !== undefined,
		[client],
	);
	const clientIsMOAValue = useWatch({
		control,
		name: "clientIsMOA",
	});
	const clientValue = useWatch({
		control,
		name: "clientSociety",
	});
	const clientSocietyIsSyndicValue = useWatch({
		control,
		name: "clientSociety.isSyndic",
	});

	useClientAutoSelection({
		client,
		setSelectedClient,
		initialClientSocietyId,
	});

	return (
		<div className="flex flex-col gap-4 w-full">
			{!currentUserIsClient && (
				<Button
					className="self-start text-[#144C65] text-xl"
					variant="link"
					onClick={() => {
						setSelectedClient(undefined);
						reset();
					}}
				>
					<ArrowLeft /> Retour
				</Button>
			)}
			{!client ? (
				<NewClientForm isMOAClient={false} excludeSyndics={excludeSyndics} />
			) : (
				<>
					{isClientUsers(client) ? (
						client.map((client) => (
							<div
								key={client.id}
								className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-4 gap-4"
							>
								<div className="flex flex-row gap-4 w-full">
									<Input value={client.firstname} label="Prénom" disabled />
									<Input value={client.lastname} label="Nom" disabled />
								</div>
								<div className="flex flex-row gap-4 w-full">
									<Input value={client.email || ""} label="Email" disabled />
									<Input
										value={client.mobilePhone || ""}
										label="Téléphone"
										disabled
									/>
								</div>

								<div className="flex flex-row gap-4 w-full">
									<Input
										value={client.address?.streetNumber ?? ""}
										label="Numéro de rue"
										disabled
									/>
									<Input
										value={client.address?.streetName ?? ""}
										label="Nom de rue"
										disabled
									/>
								</div>

								<div className="flex flex-row gap-4 w-full">
									<Input
										value={client.address?.postalCode ?? ""}
										label="Code postal"
										disabled
									/>
									<Input
										value={client.address?.city ?? ""}
										label="Ville"
										disabled
									/>
								</div>
							</div>
						))
					) : (
						<>
							<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-4 gap-4">
								<Input value={client.siren ?? ""} label="SIREN" disabled />
								<Input
									value={client.denomination}
									label="Raison sociale"
									disabled
								/>

								{hasHeadOfficeAddress ? (
									<>
										<div className="flex flex-row gap-4 w-full">
											<Input
												value={client.headOfficeAddress?.streetNumber ?? ""}
												label="Numéro de rue"
												disabled
											/>
											<Input
												value={client.headOfficeAddress?.streetName ?? ""}
												label="Nom de rue"
												disabled
											/>
										</div>
										<div className="flex flex-row gap-4 w-full">
											<Input
												value={client.headOfficeAddress?.postalCode ?? ""}
												label="Code postal"
												disabled
											/>
											<Input
												value={client.headOfficeAddress?.city ?? ""}
												label="Ville"
												disabled
											/>
										</div>
									</>
								) : (
									<AddressFormFields
										fieldPrefix="clientSociety"
										showBorder={false}
									/>
								)}
							</div>
							{!currentUserIsClient &&
								(canUpdateExistingSociety ? (
									<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-4 gap-4">
										<ControlledCheckbox
											control={control}
											id="clientSociety-isSyndic"
											name="clientSociety.isSyndic"
											label="Il s'agit d'un syndic de copropriété"
										/>
										{Boolean(clientSocietyIsSyndicValue) && (
											<IsSyndicContainer formKey="clientSociety" />
										)}
									</div>
								) : (
									<p className="text-sm text-[#144C65]">
										Pour modifier ce client, contactez Dune.
									</p>
								))}
						</>
					)}
				</>
			)}
			<ControlledRadioGroup
				control={control}
				name="clientIsMOA"
				label="Le client est également le MOA"
				items={[
					{ id: "client-moa", value: "true", label: "Oui" },
					{ id: "client-not-moa", value: "false", label: "Non" },
				]}
				disabled={currentUserIsClient}
			/>
			{clientIsMOAValue !== "true" && <NewClientForm isMOAClient={true} />}

			{canValidate ? (
				<div className="flex self-end gap-4">
					{!clientValue?.isSyndic && (
						<Tooltip>
							<TooltipTrigger className="self-end" asChild>
								<Button
									variant="outlineBlue"
									size="lg"
									type="button"
									onClick={async () => {
										const isValid = await trigger();
										if (isValid) {
											const formValues = getValues();
											const parseResult =
												createConstructionSiteSchema.safeParse(formValues);
											if (parseResult.success) {
												setIsModalToQuizzOpen(true);
											}
										}
									}}
									disabled={
										!!clientValue && !!clientValue.isSyndic && excludeSyndics
									}
								>
									Envoyer le formulaire à mon client
								</Button>
							</TooltipTrigger>
							{excludeSyndics && (
								<TooltipContent>
									<p>
										Vous ne pouvez pas créer un chantier GFA pour un syndic.
									</p>
								</TooltipContent>
							)}
						</Tooltip>
					)}

					<Tooltip>
						<TooltipTrigger className="self-end" asChild>
							<Button
								size="lg"
								disabled={
									!!clientValue && !!clientValue.isSyndic && excludeSyndics
								}
							>
								Compléter le questionnaire
								<ArrowRight />
							</Button>
						</TooltipTrigger>
						{excludeSyndics && (
							<TooltipContent>
								<p>Vous ne pouvez pas créer un chantier GFA pour un syndic.</p>
							</TooltipContent>
						)}
					</Tooltip>
				</div>
			) : (
				<div className="flex self-end gap-4">
					{!clientValue?.isSyndic && (
						<Tooltip>
							<TooltipTrigger className="self-end" asChild>
								<Button
									variant="outlineBlue"
									size="lg"
									type="button"
									disabled={true}
								>
									Envoyer le formulaire à mon client
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>
									Vous devez sélectionner un apporteur pour envoyer le
									formulaire.
								</p>
							</TooltipContent>
						</Tooltip>
					)}
					<Tooltip>
						<TooltipTrigger className="self-end" asChild>
							<Button type="button" size="lg" disabled={true}>
								Compléter le questionnaire
								<ArrowRight />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<p>
								Vous devez sélectionner un apporteur pour compléter le
								questionnaire.
							</p>
						</TooltipContent>
					</Tooltip>
				</div>
			)}
			<SendQuizToCreateConstructionByClientDialog
				open={isModalToQuizzOpen}
				onOpenChange={(open) => {
					setIsModalToQuizzOpen(open);
				}}
			/>
		</div>
	);
};

export default ClientDetailsContainer;
